import Link from 'components/base/Link'
import { c, FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useIntl } from 'lib/intl-utils'
import { useRouter } from 'next/router'
import { findActiveNavItem } from 'components/sidebar/lib/navItems'
import Tooltip from 'components/base/Tooltip'

const MallPicker: FC = () => {
  const ctx = useUserContext()
  const { user, malls, theme, currentMall } = ctx
  const { t, format } = useIntl()
  const router = useRouter()

  const activeNavItem = findActiveNavItem(router)
  const largeFormat = malls.length < 15

  const mallGroups = [
    {
      name: 'marketplaces',
      codes: ['wb', 'ozon', 'beru', 'goods', 'uzum'],
    },
    {
      name: 'electronics',
      codes: ['dns', 'citilink', 'mvideo', 'eldorado', 'holodilnik'],
    },
    {
      name: 'food',
      codes: ['lavka', 'samokat', 'lenta', 'magnit_market', 'perekrestok', 'vprok', 'ozon_fresh'],
    },
    {
      name: 'pharmacies',
      codes: ['apteka', 'zdravcity', 'goldapple', 'magnit_cosmetic'],
    },
    {
      name: 'other',
      codes: ['lamoda', 'detmir'],
    },
  ]

  const sortedMalls = (() => {
    const mallOrder = mallGroups.flatMap((group) => group.codes)

    return malls.sort((a, b) => {
      const indexA = mallOrder.indexOf(a.code)
      const indexB = mallOrder.indexOf(b.code)

      if (indexA === -1 && indexB === -1) {
        return a.code.localeCompare(b.code)
      }
      if (indexA === -1) return 1
      if (indexB === -1) return -1

      return indexA - indexB
    })
  })()

  const renderMall = (mall: (typeof malls)[0]) => {
    const active = mall.code === currentMall.code
    const productsCount = mall.stat.productsCount

    return (
      <Tooltip
        content={mall.description}
        disabled={largeFormat}
        side="bottom"
        disableHoverableContent
      >
        <Link
          key={mall.code}
          href={
            activeNavItem &&
            (activeNavItem.customVisibilityCondition?.(ctx) ||
              (!activeNavItem.customVisibilityCondition &&
                user.roles.includes(activeNavItem.role) &&
                (!activeNavItem.malls || activeNavItem.malls.includes(mall.code)) &&
                (!activeNavItem.hosts || activeNavItem.hosts.includes(theme.hostType)))) &&
            activeNavItem.addMall
              ? `${activeNavItem.route}${mall.code}/`
              : `/products/list/${mall.code}/`
          }
          className={c`group font-medium text-gray-700 relative ${largeFormat} py-0.5`}
        >
          <div
            className={c`relative grow rounded-lg flex items-center ${active} bg-primary-100 group-hover:bg-primary-200 | group-hover:bg-gray-100 ${largeFormat} px-3 py-2.5 gap-2.5 | p-1.5 flex-col items-center gap-1`}
          >
            <img
              src={`/img/mall_ico/${mall.code}.png`}
              className={c`relative object-contain ${largeFormat} size-[18px] | size-[28px]`}
              alt={`${mall.description} logo`}
            />
            <span className={c`${largeFormat} text-sm | text-[10px] text-center leading-3 hidden`}>
              {mall.description}
            </span>
            {productsCount !== null && largeFormat && (
              <Tooltip content={t`products`}>
                <div className="w-max top-1/2 -translate-y-1/2 right-2 px-1 py-px border border-gray-200/30 font-medium bg-gray-600/5 absolute text-[10px] text-gray-500 rounded-md">
                  <span>{format.number(productsCount)}</span>
                </div>
              </Tooltip>
            )}
            {productsCount === null && largeFormat && (
              <div className="w-max top-[-1px] right-[-1px] pt-1 pr-1.5 border-b border-l border-red-200/40 font-medium bg-red-600/5 pb-0.5 px-1 absolute text-[10px] text-red-500 rounded-tr-lg rounded-bl-lg">
                {t`not working`}
              </div>
            )}
          </div>
        </Link>
      </Tooltip>
    )
  }

  if (largeFormat) {
    return <div className="px-2 py-0.5 flex flex-col">{sortedMalls.map(renderMall)}</div>
  }

  return (
    <div className="pl-4 pr-2 py-0.5 flex flex-wrap gap-0.5 items-stretch">
      {mallGroups.map((group, groupIndex) => {
        let groupMalls = sortedMalls.filter((mall) => group.codes.includes(mall.code))

        if (group.name === 'other') {
          const otherMalls = sortedMalls.filter(
            (mall) => !mallGroups.some((g) => g.codes.includes(mall.code))
          )
          groupMalls = [...groupMalls, ...otherMalls]
        }

        if (groupMalls.length === 0) return null

        return (
          <div key={group.name} className="contents">
            <div className="w-full text-xs text-gray-600 font-medium px-1 py-1 leading-3 mt-3">
              {t(`sidebar.mall_groups.${group.name}`)}
            </div>
            {groupMalls.map(renderMall)}
            {groupIndex < mallGroups.length - 1 && (
              <div key={`break-${groupIndex}`} className="w-full h-0" />
            )}
          </div>
        )
      })}
    </div>
  )
}

export default MallPicker
